"use client";

import { useEffect, useRef, useState } from "react";

import { SeparationTypeSelector } from "@/app/(application)/(normal)/stem-splitter/components/input/SeparationTypeSelector";
import { Button } from "@/components/buttons/Button";
import { FileCoverInput } from "@/components/fileCoverInput/FileCoverInput";
import { Skeleton } from "@/components/skeletons/Skeleton";
import { Heading3 } from "@/components/text/Heading3";
import { SoftText } from "@/components/text/SoftText";
import { FilePresentation } from "@/components/uploadFilePresentation/FilePresentation";
import { MINUTES_MAX_UPLOAD_STEM_SPLIT_GROUP } from "@/constants/constants";
import { stemSplitNamespace } from "@/constants/namespaces";
import { STEM_SPLIT_GROUP } from "@/constants/stemSplit";
import { createStemSplitGroupAction } from "@/data/stemSplit/actions/createStemSplitGroupAction";
import { useAudioInput } from "@/lib/zustand/useAudioInput";
import { StemSplitGroupType } from "@/types/defs/stemSplit";
import { AudioInputState, UserNoDates } from "@/types/types";
import { logError } from "@/utils/logError";
import { isSelectedFile } from "@/utils/selectedFileCover/is/isSelectedFile";
import { isValidLengthSubscription } from "@/utils/subscriptionLevels/time/isValidLengthSubscription";

export function Input({
  user,
  shouldShowLoadState,
}: {
  user: UserNoDates | null;
  shouldShowLoadState: boolean;
}) {
  const useStemSplitInput = useAudioInput((state) =>
    state.getNamedStore(stemSplitNamespace)
  );
  const selectedFileCover = useStemSplitInput(
    (state) => state.selectedFileCover
  );
  const clearInput = useStemSplitInput((state) => state.clearInput);
  const setInputFile = useStemSplitInput((state) => state.setInputFile);
  const hasUser = user !== null;

  const selectedFileCoverRef =
    useRef<AudioInputState["selectedFileCover"]>(selectedFileCover);

  const [submitting, setSubmitting] = useState(false);
  const [stemSplitType, setStemSplitType] = useState<StemSplitGroupType>(
    STEM_SPLIT_GROUP.DrumsBass
  );
  const handleSubmit = async () => {
    if (
      !hasUser ||
      selectedFileCover === null ||
      !isSelectedFile(selectedFileCover)
    ) {
      return;
    }

    setSubmitting(true);

    try {
      // Handling file uploading (allows user to submit before file is uploaded)
      const maxWaitTime = 60000; // 60 seconds
      const checkInterval = 100; // 100ms
      let elapsedTime = 0;

      while (
        selectedFileCoverRef.current === null ||
        !isSelectedFile(selectedFileCoverRef.current) ||
        selectedFileCoverRef.current.selectedSound === null ||
        selectedFileCoverRef.current.progress !== null
      ) {
        if (elapsedTime >= maxWaitTime) {
          throw new Error("File upload timeout - please try again");
        }

        await new Promise((resolve) => setTimeout(resolve, checkInterval));
        elapsedTime += checkInterval;
      }

      const currentFile = selectedFileCoverRef.current;

      if (
        currentFile === null ||
        !isSelectedFile(currentFile) ||
        currentFile.selectedSound === null
      ) {
        return;
      }

      const lengthS = currentFile.selectedSound.lengthS;

      await createStemSplitGroupAction({
        inputSoundId: currentFile.selectedSound.id,
        stemSplitType,
        filename: currentFile.selectedSound.filename,
        lengthS,
        inputSoundPresignedUrl: currentFile.selectedSound.presignedUrlDownload,
        originalFileFormat: currentFile.originalFileFormat,
      });

      if (user.conversionTimeLeftSeconds !== null) {
        const validAudioLength = isValidLengthSubscription(
          user.conversionTimeLeftSeconds - lengthS,
          lengthS
        );

        if (!validAudioLength) {
          clearInput();
        }
      }
    } catch (error) {
      logError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const onStemSplitTypeChange = (newType: StemSplitGroupType) => {
    setStemSplitType(newType);
  };

  const onFileChange = async ({
    file,
    isFirstFile,
  }: {
    file: File;
    isFirstFile: boolean;
  }) => {
    await setInputFile({ file, isFirstFile });
  };

  const shouldShowSplitButton =
    selectedFileCover !== null && isSelectedFile(selectedFileCover);

  useEffect(() => {
    selectedFileCoverRef.current = selectedFileCover;
  }, [selectedFileCover]);

  return (
    <div className="xl:min-w-1/2 relative h-fit w-full flex-1 rounded-3xl bg-gray-50 px-4 py-4 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:px-8 sm:py-8 xl:w-1/2">
      <Heading3 elementType="h3">Input</Heading3>
      <SoftText elementType="p" className="mb-4 mt-1">
        Upload your audio file and select the separation variant. The AI will
        split your audio into separate stems.
      </SoftText>

      <div className="space-y-6">
        {shouldShowLoadState ?
          <Skeleton className="h-48 w-full rounded-3xl" />
        : <FileCoverInput
            user={user}
            checkSubscription={true}
            audioContext={null}
            audioLengthMinLimitMinutes={null}
            audioLengthMaxLimitMinutes={MINUTES_MAX_UPLOAD_STEM_SPLIT_GROUP}
            showRecorder={false}
            adjustAcoustic={null}
            showSelectCoverText={null}
            showCover={false}
            setInputCover={null}
            setInputFiles={[onFileChange]}
            presentation={
              selectedFileCover !== null && isSelectedFile(selectedFileCover) ?
                <FilePresentation
                  selectedFile={selectedFileCover}
                  clearInput={clearInput}
                  hideClearButton={submitting}
                  footerSlot={null}
                />
              : null
            }
            currentAudioLengthS={0}
            multipleFiles={false}
            maxMultiAudioLengthMinutes={null}
            empty={selectedFileCover === null}
          />
        }

        <SeparationTypeSelector
          selectedVariant={stemSplitType}
          onVariantChange={onStemSplitTypeChange}
        />
      </div>

      {shouldShowSplitButton && (
        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            loading={submitting}
            variant="primary"
          >
            Split
          </Button>
        </div>
      )}
    </div>
  );
}
